import { fonts, FontSelectionMap, TextLeaf } from '@typedream/core';
import { getNode } from '@udecode/plate-core';
import { memo, useEffect, useState } from 'react';
import shallow from 'zustand/shallow';
import { SelectFont, SelectFontItem } from '../../../components/SelectFont';
import { DEFAULT_FONT_FAMILY } from '../../../nodes/text/defaults';
import { setFontFamily } from '../../../nodes/text/transforms';
import { useTPlateEditorRef } from '../../../plate';
import { useDreamEditorStore } from '../../../plugins/store';
import { getFontListFromEditor } from '../../../utils/getFontListFromEditor';
import styles from '../SideToolbar.module.css';

function FontFamily() {
  const editor = useTPlateEditorRef();
  const [filterValue, setFilterValue] = useState('');

  const [currentFontFamily, isDisabled] = useDreamEditorStore((state) => {
    let fontFamily = DEFAULT_FONT_FAMILY;

    if (state.editor?.selection) {
      const el = getNode<TextLeaf>(editor, state.editor.selection.focus.path);
      if (el?.fontFamily) {
        fontFamily = el.fontFamily;
      }
    }
    return [fontFamily, !state.editor?.selection];
  }, shallow);

  const [fontFamiliesMap, setFontFamiliesMap] = useState(fonts);
  const fontFamilyName =
    fontFamiliesMap[currentFontFamily as keyof typeof fonts] || currentFontFamily;

  // TODO (@resir014): There are multiple places this font-loading logic needs to be changed.
  // Find a way to consolidate them. So far we've found these places:
  // - Navbar options
  // - Side toolbar
  // - Dream editor
  useEffect(() => {
    const loadFonts = async () => {
      try {
        let fontMap: FontSelectionMap = {};
        const res = await fetch('/api/fonts', {
          method: 'GET',
        });

        const data = await res.json();
        // eslint-disable-next-line no-restricted-syntax
        for (const font of data.items) {
          if (font.family) {
            fontMap = { ...fontMap, [font.family]: font.family };
          }
        }
        setFontFamiliesMap({ ...fonts, ...fontMap });
      } catch (e) {
        console.error(e);
      }
    };

    loadFonts();
  }, []);

  return (
    <div className={styles.section}>
      <span className={styles.label}>Font Family</span>
      <SelectFont
        triggerLabel={
          <span
            style={{
              fontFamily: fontFamilyName,
            }}
          >
            {fontFamilyName}
          </span>
        }
        disabled={isDisabled}
        onSearch={setFilterValue}
        usedFonts={getFontListFromEditor(editor.children).map((k) => (
          <SelectFontItem
            key={k}
            active={currentFontFamily === k}
            onClick={() => {
              setFontFamily(editor, k as keyof typeof fonts);
            }}
            role="none"
            data-testid={`Toolbar_font_${k}`}
          >
            {fontFamiliesMap[k as keyof typeof fonts] || k}
          </SelectFontItem>
        ))}
      >
        {Object.entries(fontFamiliesMap)
          .filter(([k]) => k !== 'default')
          .filter(([, v]) =>
            filterValue
              ? v?.trim().toLowerCase().includes(filterValue.trim().toLocaleLowerCase())
              : true
          )
          .map(([k, v]) => (
            <SelectFontItem
              key={k}
              active={currentFontFamily === k}
              onClick={() => {
                setFontFamily(editor, k as keyof typeof fonts);
              }}
              role="none"
            >
              {v}
            </SelectFontItem>
          ))}
        <SelectFontItem
          onClick={() => {
            setFontFamily(editor, filterValue as keyof typeof fonts);
          }}
          role="none"
          data-testid="Toolbar_font_specified"
        >
          Specify &quot;{filterValue}&quot;
        </SelectFontItem>
      </SelectFont>
    </div>
  );
}

export default memo(FontFamily);
